import React, { useEffect } from 'react';
import { DimensionValue, View, ViewStyle } from 'react-native';
import Animated, { useAnimatedStyle, useSharedValue, withRepeat, withTiming } from 'react-native-reanimated';
import { radius, useAppTheme } from '@/theme';
import { ScaledSheet } from '@/theme/responsive';

interface Props {
  width?: DimensionValue;
  height?: number;
  rounded?: keyof typeof radius;
  style?: ViewStyle;
}

export function Skeleton({ width = '100%', height = 16, rounded = 'sm', style }: Props) {
  const { colors } = useAppTheme();
  const opacity = useSharedValue(0.5);

  useEffect(() => {
    opacity.value = withRepeat(withTiming(1, { duration: 700 }), -1, true);
  }, [opacity]);

  const animatedStyle = useAnimatedStyle(() => ({ opacity: opacity.value }));

  return (
    <Animated.View
      style={[{ width, height, borderRadius: radius[rounded], backgroundColor: colors.surfaceMuted }, animatedStyle, style]}
    />
  );
}

/** Common skeleton compositions so screens don't hand-roll their own loading shapes. */
export function CardSkeleton() {
  return (
    <View style={styles.card}>
      <Skeleton height={140} rounded="md" />
      <View style={styles.cardBody}>
        <Skeleton width="70%" height={16} />
        <Skeleton width="45%" height={13} />
        <Skeleton width="30%" height={13} />
      </View>
    </View>
  );
}

export function ListRowSkeleton() {
  return (
    <View style={styles.row}>
      <Skeleton width={56} height={56} rounded="pill" />
      <View style={styles.rowBody}>
        <Skeleton width="60%" height={15} />
        <Skeleton width="40%" height={12} />
      </View>
    </View>
  );
}

const styles = ScaledSheet.create({
  card: { gap: 10, width: 220 },
  cardBody: { gap: 6 },
  row: { flexDirection: 'row', alignItems: 'center', gap: 12, paddingVertical: 10 },
  rowBody: { flex: 1, gap: 6 },
});
